import { Box, Stack } from '@mui/material';
import React, { ReactNode } from 'react';
import SignatureCardHeader from '../CardHeader';
import {
  SignatureCardProps,
  SignatureTypeTitle,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/MailSignature';
import { NewLogoURL } from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/MailSignature/@hooks/constants';
import { useTranslation } from 'react-i18next';

const BorderPaddingSize = 16;
const BorderPadding = `${BorderPaddingSize}px`;

const VerticalStyle = ({
  data: {
    name,
    companyName,
    divisionName,
    positionName,
    departmentName,
    companyAddress,
    phoneNumber,
    email,
    insidePhone,
    logoUrl,
  },
  copyMailSignature,
  wrapperStyle,
}: SignatureCardProps) => {
  const { t } = useTranslation();
  const imgUrlChk = (url?: string) =>
    url?.startsWith('http') ? url : `https://${url}`;

  const id = 'vertical_style_namecard';

  return (
    <Stack sx={wrapperStyle}>
      <SignatureCardHeader
        title={t(`MYPAGE.${SignatureTypeTitle[id]}`)}
        copyButtonOnClick={() => copyMailSignature(id)}
      />
      <Box
        sx={{
          padding: '12px',
          borderRadius: '8px',
          background: '#F3F5F6',
        }}
      >
        <table
          id={id}
          cellPadding="0"
          cellSpacing="0"
          width="100%"
          style={{
            borderCollapse: 'collapse',
            fontFamily: 'Pretendard, sans-serif',
            background: '#fff',
            border: 0,
            tableLayout: 'fixed',
            maxWidth: `320px`,
          }}
        >
          {/*  가장 상단 로고 영역 */}
          <thead>
            <tr>
              {/*  (좌측) 회사 로고 */}
              <td
                style={{
                  verticalAlign: 'center',
                  paddingTop: BorderPadding,
                  paddingLeft: BorderPadding,
                }}
              >
                <img
                  src={imgUrlChk(logoUrl)}
                  alt="logo"
                  style={{
                    width: '100%',
                    maxWidth: '84px',
                    height: 'auto',
                  }}
                />
              </td>
              {/*  (우측) 디자인 로고 */}
              <td
                style={{
                  textAlign: 'right',
                  verticalAlign: 'center',
                  maxWidth: `${84 + BorderPaddingSize}px`,
                  paddingTop: BorderPadding,
                  paddingRight: BorderPadding,
                }}
              >
                <img
                  src={NewLogoURL}
                  alt={'Attractive Active in life logo'}
                  style={{
                    width: '100%',
                    maxWidth: '84px',
                    height: 'auto',
                  }}
                />
              </td>
            </tr>
            <tr>
              <HorizontalDivider
                type={'primary'}
                sidePadding={BorderPadding}
                colSpan={2}
              />
            </tr>
          </thead>

          {/*  인사 정보 */}
          <tbody>
            {/* (한국어) 이름 및 직급 */}
            <tr>
              <td
                colSpan={2}
                style={{
                  paddingLeft: BorderPadding,
                  paddingBottom: '4px',
                  verticalAlign: 'baseline',
                }}
              >
                <span
                  style={{
                    color: '#000',
                    fontSize: '18px',
                    fontWeight: 500,
                    lineHeight: '24px',
                    letterSpacing: '4px',
                  }}
                >
                  {name.ko}
                </span>
                <span
                  style={{
                    color: '#000',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    marginLeft: '4px',
                  }}
                >
                  {positionName.ko}
                </span>
              </td>
            </tr>
            {/* (영어) 이름 및 직급 */}
            <tr>
              <td
                colSpan={2}
                style={{
                  paddingLeft: BorderPadding,
                  paddingBottom: '16px',
                  verticalAlign: 'middle',
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                }}
              >
                {name.en}
                {name.en && <LetterDivider type={'secondary'} />}
                {positionName.en}
              </td>
            </tr>

            {/* 소속 */}
            <tr>
              <td
                colSpan={2}
                style={{
                  paddingLeft: BorderPadding,
                  verticalAlign: 'middle',
                  color: '#000',
                  fontSize: '14px',
                  fontWeight: 600,
                  lineHeight: '20px',
                }}
              >
                {divisionName.ko}
                {divisionName.ko && <LetterDivider type="primary" />}
                {departmentName}
              </td>
            </tr>

            <tr>
              <HorizontalDivider
                type={'secondary'}
                sidePadding={BorderPadding}
                colSpan={2}
              />
            </tr>

            {/* 개인정보 */}
            <tr>
              <td
                colSpan={2}
                style={{
                  padding: `0 ${BorderPadding} ${BorderPadding} ${BorderPadding}`,
                }}
              >
                <table
                  cellPadding="0"
                  cellSpacing="0"
                  width="100%"
                  style={{
                    borderCollapse: 'collapse',
                    fontFamily: 'Pretendard, sans-serif',
                    background: '#fff',
                    border: 0,
                    tableLayout: 'fixed',
                  }}
                >
                  <tbody>
                    {[
                      {
                        type: 'tel',
                        label: 'M',
                        value: phoneNumber,
                        bottomPadding: 12,
                      },
                      {
                        type: 'tel',
                        label: 'T',
                        value: insidePhone,
                        bottomPadding: 12,
                      },
                      {
                        type: 'mailto',
                        label: 'E',
                        value: email,
                        bottomPadding: 0,
                      },
                      {
                        type: 'divider',
                        label: 'divider',
                        value: 'divider',
                        bottomPadding: 12,
                      },
                      {
                        type: 'text',
                        label: 'A',
                        value: (
                          <>
                            {companyName.en}
                            <br />
                            {companyAddress}
                          </>
                        ),
                        bottomPadding: 0,
                      },
                    ]
                      .filter((item) => item.value)
                      .map((item) => {
                        if (item.type === 'divider') {
                          return (
                            <tr key={item.label}>
                              <HorizontalDivider
                                key={item.label}
                                type={'secondary'}
                                colSpan={2}
                              />
                            </tr>
                          );
                        }

                        return <LabelAndValue key={item.label} {...item} />;
                      })}
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </Box>
    </Stack>
  );
};

export default VerticalStyle;

export const LabelAndValue = (props: {
  type: string;
  label: string;
  value: ReactNode;
  bottomPadding: number;
}) => {
  const { type, label, value, bottomPadding } = props;
  const paddingBottom = `${bottomPadding}px`;

  const cellCssStyle = {
    verticalAlign: 'top',
    color: '#000',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
  };

  const linkCssStyle = {
    color: '#000',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    padding: 0,
    margin: 0,
    fontDecoration: 'none',
  };

  return (
    <tr>
      <td
        style={{
          paddingBottom,
          width: '24px',
          textAlign: 'center',
          ...cellCssStyle,
        }}
      >
        {label}
      </td>
      <td
        style={{
          paddingBottom,
          paddingLeft: '12px',
          ...cellCssStyle,
        }}
      >
        {type === 'text' && (
          <span style={{ wordBreak: 'break-word' }}>{value}</span>
        )}
        {type === 'mailto' && (
          <a
            href={`mailto:${value}`}
            target={'_blank'}
            rel="noreferrer"
            style={linkCssStyle}
          >
            <span style={{ wordBreak: 'break-word' }}>{value}</span>
          </a>
        )}
        {type === 'tel' && (
          <a
            href={`tel:${value}`}
            target={'_blank'}
            rel="noreferrer"
            style={linkCssStyle}
          >
            <span style={{ wordBreak: 'break-word' }}>{value}</span>
          </a>
        )}
      </td>
    </tr>
  );
};

export const LetterDivider = (props: { type: 'primary' | 'secondary' }) => {
  const isPrimary = props.type === 'primary';

  return (
    <span
      style={{
        color: isPrimary ? '#000' : '#D4D7DB',
        fontWeight: `${isPrimary ? 2 : 1}px`,
        verticalAlign: 'middle',
      }}
    >
      &nbsp;&nbsp;|&nbsp;&nbsp;
    </span>
  );
};

export const HorizontalDivider = (props: {
  type: 'primary' | 'secondary' | 'small-secondary';
  colSpan: number;
  sidePadding?: string;
}) => {
  const isPrimary = props.type === 'primary';
  const height = isPrimary ? 16 : props.type === 'small-secondary' ? 8 : 12;

  return (
    <>
      <td
        colSpan={props.colSpan}
        style={{
          padding: `0 ${props.sidePadding ?? '0px'}`,
        }}
      >
        <table
          cellPadding="0"
          cellSpacing="0"
          width="100%"
          style={{
            border: 0,
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <tbody>
            <tr>
              <td
                style={{
                  height: `${height}px`,
                }}
              />
            </tr>
            <tr>
              <td
                style={{
                  height: '1px',
                  padding: `0 ${props.sidePadding ?? '0px'}`,
                  backgroundColor: isPrimary ? '#000' : '#E7E9EC',
                }}
              >
                <div
                  style={{
                    height: '1px',
                    fontSize: '1px',
                    lineHeight: '1px',
                  }}
                >
                  &nbsp;
                </div>
              </td>
            </tr>
            <tr>
              <td
                style={{
                  height: `${height}px`,
                }}
              />
            </tr>
          </tbody>
        </table>
      </td>
    </>
  );
};
